import { z } from "zod";

import type { Locale, WidgetTheme } from "./wire";

export const LOCALES = ["id", "en"] as const satisfies readonly Locale[];

const localized = z.object({ id: z.string().max(400), en: z.string().max(400) });
const localizedList = z.object({
  id: z.array(z.string().max(120)).max(6),
  en: z.array(z.string().max(120)).max(6),
});

/**
 * Who the bot is. Everything a specialist sets in "Persona & Widget" — the
 * catalog promises name, greeting, and speaking style tailored to the
 * business, plus topics it must not touch.
 */
export const personaSchema = z.object({
  name: z.string().min(1).max(40),
  role: z.string().max(80),
  tone: z.enum(["hangat", "profesional", "santai", "formal"]),
  greeting: localized,
  suggestions: localizedList,
  /** Free-form style notes, e.g. "panggil pengunjung 'Kak', boleh pakai emoji secukupnya". */
  styleNotes: z.string().max(600),
  /** Topics the bot declines and hands to a human (e.g. diagnosa medis, nego harga grosir). */
  forbiddenTopics: z.array(z.string().max(120)).max(20),
  /** Retired with the Leads section: kept so stored personas still parse; never read. */
  leadCapture: z.boolean(),
});
export type Persona = z.infer<typeof personaSchema>;

export const widgetThemeSchema = z.object({
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  accentContrast: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  surface: z.enum(["light", "dark", "auto"]),
  radius: z.number().int().min(0).max(28),
  position: z.enum(["right", "left"]),
  /** Font stack inherited from the host page when empty. */
  fontFamily: z.string().max(200),
  launcherLabel: localized,
  /**
   * Set by the Console's upload (a gateway asset path, /v1/assets/…). An https
   * URL from before uploads existed is still accepted.
   */
  avatarUrl: z.string().regex(/^\/v1\/assets\/[A-Za-z0-9/_.-]+$/).or(z.url({ protocol: /^https$/ })).or(z.literal("")),
  avatarMode: z.enum(["photo", "initial", "none"]),
  launcherStyle: z.enum(["pill", "bubble", "square", "teaser"]),
  launcherIcon: z.enum(["chat", "sparkle", "help", "wave"]),
  animation: z.enum(["pop", "slide", "fade", "spring"]),
  attention: z.enum(["none", "pulse", "bounce", "wiggle"]),
  languageSwitch: z.boolean(),
}) satisfies z.ZodType<WidgetTheme>;

export const limitsSchema = z.object({
  messagesPerVisitorPerDay: z.number().int().min(1).max(500),
  messagesPerIpPerDay: z.number().int().min(1).max(5000),
  maxMessageChars: z.number().int().min(50).max(4000),
  maxOutputTokens: z.number().int().min(100).max(2000),
  historyTurns: z.number().int().min(0).max(20),
  requestsPerMinute: z.number().int().min(1).max(600),
});
export type Limits = z.infer<typeof limitsSchema>;

/** Defaults match the Lampiran of Ketentuan Penggunaan API (AI-3) and Lampiran III Pasal 7. */
export const DEFAULT_LIMITS: Limits = {
  messagesPerVisitorPerDay: 30,
  messagesPerIpPerDay: 120,
  maxMessageChars: 2000,
  maxOutputTokens: 700,
  historyTurns: 8,
  requestsPerMinute: 60,
};

export const DEFAULT_THEME: WidgetTheme = {
  accent: "#0F2A47",
  accentContrast: "#FFFFFF",
  surface: "light",
  radius: 18,
  position: "right",
  fontFamily: "",
  launcherLabel: {
    id: "Yuk ngobrol, kenali kami lebih dekat!",
    en: "Let's talk and get to know us better!",
  },
  avatarUrl: "",
  avatarMode: "initial",
  launcherStyle: "pill",
  launcherIcon: "chat",
  animation: "pop",
  attention: "none",
  languageSwitch: false,
};

export function defaultPersona(businessName: string): Persona {
  return {
    name: "Asisten",
    role: `Asisten virtual ${businessName}`,
    tone: "hangat",
    greeting: {
      id: `Halo! Saya asisten virtual ${businessName}. Mau tahu apa tentang kami?`,
      en: `Hi! I'm ${businessName}'s virtual assistant. What would you like to know about us?`,
    },
    suggestions: { id: [], en: [] },
    styleNotes: "",
    forbiddenTopics: [],
    leadCapture: false,
  };
}
